/**
 * Shapes of the developer monitoring API (pizza-backend src/monitoring, /api/admin/monitoring/*).
 * Every timestamp is epoch milliseconds; latencies are milliseconds.
 */

export type MonitoringWindow = "1h" | "6h" | "24h" | "7d";
export const MONITORING_WINDOWS: MonitoringWindow[] = ["1h", "6h", "24h", "7d"];

export type Severity = "critical" | "warning" | "info";
export type OverallStatus = "ok" | "warning" | "critical";
export type FindingArea = "pods" | "pipeline" | "cameras" | "http" | "backend" | "integrations";

export interface Finding {
  code: string;
  severity: Severity;
  area: FindingArea | string;
  params: Record<string, unknown>;
  subject?: string | null;
  since?: number | null;
}

export interface HistSummary {
  n: number;
  avg: number | null;
  p50: number | null;
  p95: number | null;
  p99: number | null;
  max: number | null;
}

// --- summary ---------------------------------------------------------------------------------------------
export interface VerdictCounts {
  ok: number;
  nok: number;
  invalid_photo: number;
  no_pizza: number;
  pending: number;
  missing: number;
}

export interface SummaryKpis {
  pods: {
    total: number;
    healthy: number;
    in_flight: number;
    queued: number;
    requests_15m: number;
    failures_15m: number;
    p95_ms_15m: number | null;
  };
  cameras: {
    total: number;
    watched: number;
    online: number;
    offline: number;
    unknown: number;
    error?: string | null;
  };
  pipeline: {
    captured_1h: number;
    orders_1h: number;
    orders_1h_usual: number | null;
    captured_1h_usual: number | null;
    verdicts_1h: Partial<VerdictCounts>;
    stuck_1h: number;
    uncaptured: number;
    capture_to_verdict_1h?: HistSummary | null;
    last_capture_at: number | null;
    error?: string | null;
  };
  http: {
    requests_15m: number;
    errors_15m: number;
    client_errors_15m: number;
    p95_ms_15m: number | null;
    in_flight: number;
  };
  errors: { errors_60m: number; warnings_60m: number; errors_15m: number };
  backend: { uptime_s: number; lag_max_5m_ms: number | null; rss_mb: number | null; cpu_pct: number | null };
}

export interface Summary {
  generated_at: number;
  status: OverallStatus;
  findings: Finding[];
  kpis: SummaryKpis;
  identity: { instance: string; role: string };
  section_errors: Record<string, string>;
  build_ms: number;
}

// --- pods ------------------------------------------------------------------------------------------------
export interface PodPhases {
  at: number | null;
  path: string;
  total_ms: number;
  lock_ms: number;
  conn_ms: number;
  conn_reused: boolean;
  upload_ms: number;
  pod_ms: number;
  read_ms: number;
  in_kb: number;
  out_kb: number;
}

export interface PodSnapshot {
  endpoint: string;
  locality: string;
  healthy: boolean;
  in_flight: number;
  total: number;
  success: number;
  failure: number;
  timeout: number;
  ewma_latency_ms: number;
  consecutive_failures: number;
  deactivations: number;
  last_error: string | null;
  last_error_at: number | null;
  last_success_at: number | null;
  last_failure_at: number | null;
  health_changed_at: number | null;
  last_ping_at: number | null;
  last_ping_ms: number | null;
  last_ping_ready: boolean | null;
  idle_s: number | null;
  last_phases: PodPhases | null;
  keeper_running: boolean;
  keeper_404: boolean;
  /** [ts, latency_ms | null, ok] per request, oldest first */
  recent?: [number, number | null, boolean][];
  /** last successful GET /metrics poll of the pod (every 15 s); absent on older backends */
  metrics_at?: number | null;
  /** "unsupported" = an image without /metrics (older than v0.0.39); anything else = the last poll's error */
  metrics_error?: string | null;
  /** the pod's last GET /metrics (kept after a failed poll) */
  metrics?: PodMetrics | null;
}

// --- pod side (the pod's own GET /metrics, serving_backend core/monitoring.py) -------------------------------
export type PodMetricsWindow = "1m" | "5m" | "15m";
/** a window key is missing when it has no data */
export type PodWindows<T> = Partial<Record<PodMetricsWindow, T>>;

export interface PodDurations {
  count: number;
  rps: number;
  p50_ms: number | null;
  p90_ms: number | null;
  p99_ms: number | null;
  max_ms: number | null;
}

export type PodThrottleReason = "sw_power_cap" | "hw_slowdown" | "sw_thermal" | "hw_thermal" | "hw_power_brake";

export interface PodGpuNow {
  util_pct: number | null;
  memory_util_pct: number | null;
  memory_used_mb: number | null;
  temperature_c: number | null;
  power_w: number | null;
  sm_clock_mhz: number | null;
  throttle: (PodThrottleReason | string)[];
  /** the current PCIe link: it trains down while the GPU idles (Gen1 at idle is normal) */
  pcie_gen?: number | null;
  pcie_width?: number | null;
}

export interface PodGpu {
  available: boolean;
  name?: string;
  driver?: string;
  cuda_driver?: string;
  memory_total_mb?: number;
  power_limit_w?: number | null;
  /** the best PCIe link seen since the pod started (what the slot really gives under load) */
  pcie_gen?: number | null;
  pcie_width?: number | null;
  /** the widest link the card supports */
  pcie_max_width?: number | null;
  error: string | null;
  now: PodGpuNow | null;
  windows: PodWindows<{
    samples: number;
    util_avg_pct: number | null;
    util_max_pct: number | null;
    memory_used_max_mb: number | null;
    temperature_max_c: number | null;
    power_avg_w: number | null;
    throttled_samples: number;
  }>;
}

export interface PodHost {
  now: {
    process_cpu_pct: number | null;
    process_rss_mb: number | null;
    process_threads: number | null;
    cpu_pct: number | null;
    cpu_count: number | null;
    load_avg: [number, number, number] | null;
    memory_used_mb: number | null;
    memory_total_mb: number | null;
  } | null;
  windows: PodWindows<{
    process_cpu_avg_pct: number | null;
    process_cpu_max_pct: number | null;
    cpu_avg_pct: number | null;
    rss_max_mb: number | null;
  }>;
}

export interface PodRoute {
  total: number;
  errors_5xx: number;
  errors_4xx: number;
  in_flight: number;
  bytes_in: number;
  bytes_out: number;
  last_at: number | null;
  windows: PodWindows<PodDurations & { errors: number; client_errors: number }>;
}

export interface PodStage {
  last_ms: number | null;
  windows: PodWindows<PodDurations>;
}

export interface PodModel {
  key: "crust" | "components" | "sauces" | "obstacles" | "slicing" | string;
  file: string | null;
  loaded: boolean;
  /** e.g. ["CUDAExecutionProvider", "CPUExecutionProvider"] (ONNX Runtime) or ["cuda:0"] (torch) */
  providers: string[];
  /** crust only: nvJPEG GPU decode active */
  gpu_decode?: boolean;
}

export interface PodError {
  at: number;
  route: string;
  status: number | null;
  error: string | null;
}

export interface PodMetrics {
  schema: number;
  at: number;
  service: {
    image: string | null;
    git: string | null;
    pod_id: string | null;
    gpu_type: string | null;
    hostname: string;
    pid: number;
    started_at: number;
    uptime_s: number;
    ready: boolean | null;
    ready_after_s: number | null;
    preload_error: string | null;
  };
  gpu: PodGpu;
  host: PodHost;
  /** event-loop lag; the crust inference alone blocks the pod's loop ~150-250 ms */
  loop: { probe_s: number; windows: PodWindows<{ p99_ms: number | null; max_ms: number | null; over_100ms: number }> };
  /** by route path ("/pizza/raw", "/analyze/raw", "/ping", ...; "other" = unknown paths) */
  routes: Record<string, PodRoute>;
  /** by route path (or "startup"), then stage name ("handler.inference", "analyze.image_decode", ...) */
  stages: Record<string, Record<string, PodStage>>;
  models: PodModel[];
  /** newest first, up to 50 */
  errors: PodError[];
  /** the last 15 min, one row per sample: gpu = [at_ms, util_pct, memory_used_mb, temperature_c, power_w] */
  history: {
    sample_s: number;
    gpu_columns: string[];
    gpu: (number | null)[][];
    host_columns: string[];
    host: (number | null)[][];
  } | null;
}

export interface PoolSnapshot {
  source: "autoscaler" | "static" | "custom" | string;
  total: number;
  healthy: number;
  in_flight: number;
  queued: number;
  max_concurrency: number;
  /** worst case of one call (all attempts run out) */
  request_timeout_s: number;
  max_attempts: number;
  /** deadline of each attempt, growing (backend v0.0.285+) */
  attempt_timeouts_s?: number[];
  keeper_enabled: boolean;
  session_open: boolean;
  last_update: { at: number | null; total: number; added: string[]; removed: string[] } | null;
}

/** manual GPU pods set in the admin; with ignore_autoscaler the pool serves only these (vision.pod_override) */
export interface PodOverride {
  ignore_autoscaler: boolean;
  /** "ip:port" */
  pods: string[];
  updated_at: number | null;
  updated_by: string | null;
}

export interface PodsSection {
  pool: PoolSnapshot | null;
  pods: PodSnapshot[];
  configured: string[] | null;
  configured_docs: { ip: string; port: number; locality?: string }[];
  /** undefined on backends before v0.0.302 */
  override?: PodOverride | null;
}

// --- live ------------------------------------------------------------------------------------------------
export interface HttpRequestEntry {
  ts: number;
  method: string;
  path: string;
  route: string;
  status: number;
  ms: number;
  ip: string | null;
  device: string | null;
}

export interface LagSummary {
  current_ms: number;
  max_1m_ms: number | null;
  max_5m_ms: number | null;
  /** [ts, max lag of a 5 s period] */
  samples: [number, number][];
}

export interface ProcessSample {
  ts: number | null;
  cpu_pct: number | null;
  rss_mb: number | null;
  tasks: number | null;
  threads: number | null;
  fds: number | null;
  http_in_flight?: number;
  monitored_tasks?: number;
  background_writes?: number;
  inference_in_flight?: number;
}

export interface ProcessInfo {
  instance: string;
  role: string;
  pid: number;
  hostname: string;
  python: string;
  platform: string;
  started_at: number;
  uptime_s: number;
  version: string | null;
}

export interface LiveSection {
  generated_at: number;
  pods: PodsSection;
  http: {
    in_flight: number;
    started_total: number;
    last_minute: { n: number; s2: number; s3: number; s4: number; s5: number };
    recent: HttpRequestEntry[];
    slow: HttpRequestEntry[];
  };
  events: MonitoringEvent[];
  runtime: { lag: LagSummary; sample: ProcessSample | Record<string, never>; process: ProcessInfo };
}

// --- events ----------------------------------------------------------------------------------------------
export type EventLevel = "error" | "warning" | "info";

export interface MonitoringEvent {
  id: string;
  ts: number;
  last_ts: number | null;
  level: EventLevel;
  source: string;
  code: string;
  message: string;
  subject: string | null;
  details: Record<string, unknown> | null;
  traceback: string | null;
  pizza_id: string | null;
  branch_id: string | null;
  device_id: string | null;
  repeats: number;
  instance: string;
  role: string;
  fingerprint: string;
}

export interface EventsResponse {
  events: MonitoringEvent[];
  source: "db" | "memory";
  limit?: number;
}

export interface EventGroup {
  fingerprint: string;
  level: EventLevel;
  source: string;
  code: string;
  subject: string | null;
  message: string;
  count: number;
  events: number;
  first: number | null;
  last: number | null;
  pizzas: number;
}

export interface EventFilters {
  levels: EventLevel[];
  sources: string[];
  q: string;
  pizzaId: string;
  sinceMs: number | null;
  limit: number;
  /** exact group (source.code@subject), set when a grouped problem is opened */
  fingerprint?: string | null;
}

// --- metrics ---------------------------------------------------------------------------------------------
export interface HistSeries {
  n: (number | null)[];
  avg: (number | null)[];
  p50: (number | null)[];
  p95: (number | null)[];
  p99: (number | null)[];
  max: (number | null)[];
}

export interface RouteStat extends Omit<HistSummary, "n"> {
  route: string;
  n: number;
  s2: number;
  s3: number;
  s4: number;
  s5: number;
  error_rate: number;
  series: number[];
}

export interface MetricTotals {
  counters: Record<string, number>;
  hists: Record<string, HistSummary>;
  maxes: Record<string, number>;
  mins: Record<string, number>;
}

export interface MetricSeries {
  window: MonitoringWindow;
  start: number;
  step_ms: number;
  t: number[];
  counters: Record<string, number[]>;
  hists: Record<string, HistSeries>;
  maxes: Record<string, (number | null)[]>;
  mins: Record<string, (number | null)[]>;
  routes: RouteStat[];
  totals: MetricTotals;
}

// --- pipeline --------------------------------------------------------------------------------------------
export type Verdict = keyof VerdictCounts;

export interface PipelinePizza {
  id: string;
  name: string | null;
  branch_id: string | null;
  order_id: string | null;
  capture_at: number;
  capture_count: number;
  verdict: Verdict;
  classification: string | null;
  upload_received: boolean;
  trigger_rpc_ms: number | null;
  upload_delay_ms: number | null;
  pipeline_ms: number | null;
  capture_to_verdict_ms: number | null;
  last_event?: { code: string; message: string; level: EventLevel; source: string; ts: number } | null;
}

export interface PipelineBranch {
  id: string | null;
  name: string;
  enable_acquisition: boolean | null;
  enable_bb_trigger: boolean | null;
  orders: number;
  captured: number;
  evaluated: number;
  verdicts: VerdictCounts;
  invalid_share: number | null;
  nok_share: number | null;
  recaptures: number;
  capture_to_verdict: HistSummary;
  upload_delay: HistSummary;
  last_capture_at: number | null;
  last_order_at: number | null;
  series: number[];
}

export interface BranchMeta {
  id: string;
  name: string;
  enable_acquisition: boolean;
  enable_bb_trigger: boolean;
  enable_evaluation: boolean;
}

export interface FunnelBranch {
  id: string | null;
  ordered: number;
  packed: number;
  captured: number;
  evaluated: number;
  uncaptured: number;
}

export interface UncapturedPizza {
  id: string;
  name: string | null;
  branch_id: string | null;
  order_id: string | null;
  packed_at: number;
  bb_trigger: boolean;
}

export type PipelineLatency = "trigger_rpc_ms" | "upload_delay_ms" | "pipeline_ms" | "capture_to_verdict_ms";

export interface PipelineSection {
  window: MonitoringWindow;
  step_ms: number;
  t: number[];
  series: Record<string, (number | null)[]>;
  totals: {
    captured: number;
    orders: number;
    recaptures: number;
    verdicts: VerdictCounts;
    latency: Record<PipelineLatency, HistSummary>;
  };
  steps: ({ step: string } & HistSummary)[];
  branches: PipelineBranch[];
  branches_meta: BranchMeta[];
  recent: PipelinePizza[];
  missing: PipelinePizza[];
  missing_total: number;
  baseline?: { days: number; captured: number[]; orders: number[] };
  funnel: {
    since: number;
    orders: number;
    totals: Omit<FunnelBranch, "id">;
    branches: FunnelBranch[];
    uncaptured: UncapturedPizza[];
  };
  query_ms: number;
  generated_at: number;
}

// --- cameras ---------------------------------------------------------------------------------------------
export interface CameraConnectivityInfo {
  active: boolean | null;
  last_connect_time: number | null;
  last_disconnect_time: number | null;
  last_activity_time: number | null;
  inactivity_alarm_time: number | null;
}

export interface CameraActivity {
  triggers?: number;
  trigger_failures?: number;
  uploads?: number;
  upload_rejections?: number;
  flag_polls?: number;
  last_trigger_at?: number | null;
  last_trigger_ok?: boolean;
  last_trigger_ms?: number;
  last_trigger_error?: string | null;
  last_trigger_error_at?: number | null;
  last_upload_attempt_at?: number | null;
  last_upload_at?: number | null;
  last_upload_status?: number;
  last_upload_error?: string | null;
  last_upload_error_at?: number | null;
  last_upload_delay_ms?: number;
  last_flags_poll_at?: number | null;
}

export interface CameraStats {
  captured_24h: number;
  orders_24h: number;
  missing_24h: number;
  /** stuck pizzas whose photo never arrived (the rest got their photo and failed in the evaluation) */
  missing_no_photo_24h?: number;
  /** the newest stuck pizzas of the branch with the error that explains them */
  stuck?: Pick<PipelinePizza, "id" | "name" | "capture_at" | "capture_count" | "upload_received" | "last_event">[];
  invalid_share_24h: number | null;
  last_capture_at: number | null;
  last_order_at: number | null;
  upload_delay: HistSummary | null;
  capture_to_verdict: HistSummary | null;
  series: number[] | null;
  uncaptured: number;
}

export interface MonitoredCamera {
  id: string;
  name: string | null;
  label: string | null;
  type: string | null;
  created_time: number | null;
  assigned: boolean;
  branch_id: string | null;
  branch: BranchMeta | null;
  restart_camera: boolean;
  restart_service: boolean;
  connectivity: CameraConnectivityInfo | null;
  activity: CameraActivity;
  stats: CameraStats | null;
}

export interface CamerasSection {
  error?: string;
  cameras: MonitoredCamera[];
  uncovered_branches: BranchMeta[];
  thingsboard_ms?: number;
  /** bucket starts of stats.series (the 24 h pipeline window) */
  series_t?: number[] | null;
  generated_at: number;
}

// --- dependencies / runtime / instances -------------------------------------------------------------------
export interface DependencyCheck {
  name: string;
  status: "ok" | "error";
  ms: number | null;
  error?: string | null;
  kind?: "pod";
}

export interface DependenciesSection {
  checks: DependencyCheck[];
  generated_at: number;
}

export interface ModelInfo {
  name: string;
  loaded: boolean;
  version: string | null;
  threads: number | null;
  threshold: number | null;
}

export interface RuntimeSection {
  process: ProcessInfo;
  lag: LagSummary;
  sample: ProcessSample | Record<string, never>;
  samples: ProcessSample[];
  models: ModelInfo[];
  settings: Record<string, unknown>;
  monitoring: {
    persist: boolean;
    metrics_last_flush_age_s: number | null;
    metrics_last_error: string | null;
    events_last_flush_age_s: number | null;
    events_last_error: string | null;
    events_dropped: number;
    events_recorded: Record<EventLevel, number>;
  };
  integrations: Record<string, { count: number; last_at: number | null }>;
  monitored_tasks: number;
}

export interface InstanceInfo {
  instance: string;
  role: string | null;
  first: number | null;
  last: number | null;
  requests: number;
  current: boolean;
}

export interface InstancesResponse {
  instances: InstanceInfo[];
  current: string;
  role: string;
}

// --- BiteBerry webhooks ----------------------------------------------------------------------------------
export type BbKind = "order" | "item" | "status";

export interface BbStep {
  name: string | null;
  quantity_left: number | null;
}

/** One logged BiteBerry webhook (fields depend on the kind; absent ones are omitted). */
export interface BbWebhook {
  id: string;
  ts: number;
  kind: BbKind;
  outcome: string;
  detail?: string | null;
  duration_ms?: number;
  event_type?: string;
  sent_at?: string;
  order_id?: string;
  item_id?: string;
  item_ids?: string[];
  items?: { id: string | null; name: string | null; quantity: number | null; menu_item_id: string | null; steps: BbStep[] | null }[];
  menu_item_id?: string;
  name?: string;
  quantity?: number;
  branch_id?: string;
  branch_name?: string;
  status?: string;
  previous_status?: string;
  steps?: BbStep[];
  packing_left?: number | null;
  cooking_left?: number | null;
  pizza_known?: boolean;
  camera?: string;
  trigger_ms?: number;
  capture_count?: number;
  pizzas?: number;
  split?: number;
  is_test?: boolean;
  instance?: string;
  role?: string;
  payload?: unknown;
  payload_truncated?: boolean;
}

export interface BbBranch {
  id: string | null;
  name: string;
  enable_acquisition: boolean | null;
  enable_bb_trigger: boolean | null;
  orders: number;
  order_webhooks: number;
  status_webhooks: number;
  item_webhooks: number;
  packed_items: number;
  captured_items: number;
  packed_not_captured: number;
  captures: number;
  outcomes: Record<string, number>;
  errors: number;
  last_at: number | null;
}

export type BbReason =
  | "capture_time_lost"
  | "split_item"
  | "no_order"
  | "not_pizza"
  | "branch_not_found"
  | "acquisition_disabled"
  | "bb_trigger_disabled"
  | "no_camera"
  | "trigger_failed"
  | "device_error"
  | "error"
  | "capture_not_stored"
  | "packed_not_logged"
  | "never_packed"
  | "no_item_webhook";

export interface BbUncapturedPizza {
  id: string;
  name: string | null;
  branch_id: string | null;
  order_id: string | null;
  order_status: string | null;
  sent_at: number | null;
  packed_at: number | null;
  classification: string | null;
  reason: BbReason;
  logged: boolean;
  has_photo: boolean;
  hooks: number;
  packed_hooks: number;
  last_hook_at: number | null;
  last_outcome: string | null;
  last_steps: BbStep[] | null;
  detail: string | null;
}

export interface BbSection {
  window: MonitoringWindow;
  step_ms: number;
  t: number[];
  series: Record<string, number[]>;
  totals: Record<BbKind, { n: number; outcomes: Record<string, number> }>;
  packed_items: number;
  captured_items: number;
  packed_not_captured: number;
  unmatched_items: number;
  branches: BbBranch[];
  other_branches: { id: string | null; name: string | null; orders: number; webhooks: number; last_at: number | null }[];
  other_branches_total: number;
  uncaptured: {
    since: number;
    finished: number;
    captured: number;
    total: number;
    by_reason: Partial<Record<BbReason, number>>;
    by_branch: Record<string, Partial<Record<BbReason, number>>>;
    reasons: BbReason[];
    items: BbUncapturedPizza[];
  };
  log_since: number | null;
  log_ttl_days: number;
  persist: boolean;
  branches_meta: BranchMeta[];
  query_ms: number;
  generated_at: number;
}

export interface BbWebhookFilters {
  kinds: BbKind[];
  outcomes: string[];
  branchId: string | null;
  q: string;
  itemId: string | null;
  limit: number;
}

export interface BbWebhooksResponse {
  rows: BbWebhook[];
  limited: boolean;
  limit: number;
}

/** A request / response body as the mobile API log keeps it: parsed JSON, text, or the size of a binary one. */
export interface MobileApiBody {
  content_type: string | null;
  bytes: number | null;
  json?: unknown;
  text?: string;
  truncated?: boolean;
}

/** One request of the mobile API (BiteBerry tablet app, station tablets) and the backend's answer. */
export interface MobileApiRow {
  id: string;
  ts: number;
  ms: number;
  method: string;
  path: string;
  route: string | null;
  status: number;
  /** the error, the verdict and its action, or the status of the answer */
  summary: string | null;
  pizza_id: string | null;
  branch_id: string | null;
  login: string | null;
  user_id: string | null;
  ip: string | null;
  user_agent: string | null;
  request_bytes: number | null;
  response_bytes: number | null;
}

export interface MobileApiRowDetail extends MobileApiRow {
  query: string | null;
  scope: string | null;
  instance: string | null;
  /** without Authorization / Cookie */
  request_headers: Record<string, string>;
  request: MobileApiBody;
  response: MobileApiBody;
}

export interface MobileApiResponse {
  rows: MobileApiRow[];
  limited: boolean;
  limit: number;
}

export type MobileApiStatus = "2xx" | "4xx" | "5xx";

export interface BbPizzaState {
  id: string;
  name: string | null;
  cooking_finished: boolean | null;
  cooking_finished_at: number | null;
  packing_finished: boolean | null;
  packing_finished_at: number | null;
  capture_at: number | null;
  capture_count: number;
  image_received: boolean;
  evaluated_at: number | null;
  verdict: Verdict | null;
  classification: string | null;
}

export interface BbPizzaTimeline {
  item_id: string;
  base_id: string;
  order: {
    id: string | null;
    status: string | null;
    sent_at: number | null;
    terminated_at: number | null;
    branch_id: string | null;
    branch_name: string | null;
    is_test: boolean;
    items: { id: string; name: string | null; quantity: number | null }[];
  } | null;
  pizzas: BbPizzaState[];
  webhooks: BbWebhook[];
  events: MonitoringEvent[];
}

/** Why a captured pizza has no verdict (backend monitoring/unevaluated.py REASONS). */
export type UnevaluatedReason =
  | "pending"
  | "no_photo"
  | "photo_lost"
  | "no_pod"
  | "pod_failed"
  | "evaluation_error"
  | "no_record"
  | "no_record_old";

export interface UnevaluatedPizza {
  id: string;
  name: string | null;
  branch_id: string | null;
  branch_name: string | null;
  order_id: string | null;
  capture_at: number;
  capture_count: number;
  /** photo in S3; null = the check failed */
  photo: boolean | null;
  /** the backend got the upload body (it may still have refused it or failed to store it) */
  upload_received: boolean;
  /** photo in S3 and no evaluation possibly still running */
  evaluable: boolean;
  reason: UnevaluatedReason;
  event: { code: string; message: string; level: EventLevel; source: string; ts: number | null } | null;
}

export interface UnevaluatedDay {
  /** local day (YYYY-MM-DD in the section's timezone) */
  day: string;
  total: number;
  with_photo: number;
  evaluable: number;
  reasons: Record<UnevaluatedReason, number>;
  branches: { id: string | null; name: string | null; count: number }[];
  pizzas: UnevaluatedPizza[];
}

export interface UnevaluatedSection {
  generated_at: number;
  days: number;
  since: number;
  timezone: string;
  event_retention_days: number;
  /** oldest stored event: older pizzas cannot have a recorded reason */
  events_since: number | null;
  stuck_after_s: number;
  total: number;
  with_photo: number;
  evaluable: number;
  truncated: boolean;
  reasons: Record<UnevaluatedReason, number>;
  by_day: UnevaluatedDay[];
}
